import type {
	FxIdApplicationHandlersAuthAuthenticateFromSocialFacebookInstantGamesOptions
} from "../Api/gen";
import { ProblemDetailsError } from "../Api/ApiHelpers";

export const FACEBOOK_ASID_INTERNAL_ERROR_MESSAGE =
	"An internal error happened when processing this request";
export const FACEBOOK_ACCOUNT_CREATION_CONFIRMATION_REQUIRED =
	"facebook_player_account_creation_confirmation_required";

export type FacebookAuthOptions =
	FxIdApplicationHandlersAuthAuthenticateFromSocialFacebookInstantGamesOptions;
export type FacebookInstantGamesAuthEvent =
	| "asid_internal_error_recovery_started"
	| "player_info_after_asid_failed";

export interface FacebookAsidRecoveryConfig {
	enabled: boolean;
	protocolVersion: number;
}

export interface FacebookInstantGamesPlayerApi {
	getSignedASIDAsync(): Promise<FBInstant.SignedASID | null>;
	getSignedPlayerInfoAsync(requestPayload: string): Promise<FBInstant.SignedPlayerInfo | null>;
}

export const FACEBOOK_ASSOCIATED_APPS_TIMEOUT_MS = 3000;

export type FacebookAssociatedAppAsid = NonNullable<FacebookAuthOptions["AssociatedApps"]>[number];
export type FacebookAssociatedAppsUnavailableReason = "unsupported" | "empty" | "error" | "timeout";

export interface FacebookAssociatedAppsPlayerApi {
	getSignedAssociatedAppsASIDAsync?: () => Promise<FBInstant.SignedAssociatedAppASID[] | null>;
}

/**
 * Проверяет, что значение имеет объектную форму для безопасного чтения полей ошибок API.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

/**
 * Распознаёт только документированную Facebook внутреннюю ошибку получения ASID.
 */
export function isFacebookAsidInternalError(error: unknown): boolean {
	if (!isRecord(error) || typeof error.code !== "string" || typeof error.message !== "string") {
		return false;
	}

	return error.code.trim().toUpperCase() === "UNKNOWN" &&
		error.message.trim().toLowerCase() === FACEBOOK_ASID_INTERNAL_ERROR_MESSAGE.toLowerCase();
}

/**
 * Определяет ожидаемую недоступность ASID, для которой сохраняется legacy-вход по Player ID.
 */
function isAsidRetrievalBlocked(error: unknown): boolean {
	return isRecord(error) && error.code === "ASID_RETRIEVAL_BLOCKED";
}

/**
 * Формирует запрос входа по Player ID, при необходимости ограничивая его поиском существующего аккаунта.
 */
async function playerOptions(
	playerApi: FacebookInstantGamesPlayerApi,
	existingAccountOnly?: true
): Promise<FacebookAuthOptions> {
	const signedPlayer = await playerApi.getSignedPlayerInfoAsync("fxid_auth");
	if (signedPlayer === null) {
		throw new Error("Failed to get signed player info");
	}

	return {
		PlayerId: signedPlayer.getPlayerID(),
		Signature: signedPlayer.getSignature(),
		ExistingAccountOnly: existingAccountOnly
	};
}

/**
 * Строит проверенный Facebook-запрос, сохраняя legacy fallback при недоступном ASID.
 */
export async function buildFacebookInstantGamesAuthOptions(
	playerApi: FacebookInstantGamesPlayerApi,
	recoveryConfig: FacebookAsidRecoveryConfig,
	onEvent: (event: FacebookInstantGamesAuthEvent) => void = () => undefined
): Promise<FacebookAuthOptions> {
	let signedAsid: FBInstant.SignedASID | null = null;
	try {
		signedAsid = await playerApi.getSignedASIDAsync();
	} catch (error) {
		if (isAsidRetrievalBlocked(error)) {
			return playerOptions(playerApi);
		}

		if (isFacebookAsidInternalError(error) && recoveryConfig.enabled &&
			recoveryConfig.protocolVersion >= 1) {
			onEvent("asid_internal_error_recovery_started");
			return playerOptions(playerApi, true);
		}

		throw error;
	}

	const asid = signedAsid?.getASID() ?? null;
	if (signedAsid === null || asid === null || asid.trim().length === 0) {
		return playerOptions(playerApi);
	}

	try {
		const signedPlayer = await playerApi.getSignedPlayerInfoAsync("fxid_auth");
		if (signedPlayer === null) {
			throw new Error("Failed to get signed player info");
		}

		return {
			PlayerId: signedPlayer.getPlayerID(),
			ApplicationScopedId: asid,
			Signature: signedPlayer.getSignature(),
			ApplicationScopedIdSignature: signedAsid.getSignature()
		};
	} catch {
		onEvent("player_info_after_asid_failed");
		return {
			ApplicationScopedId: asid,
			Signature: signedAsid.getSignature()
		};
	}
}

/**
 * Проверяет ожидаемый ответ lookup-only без зависимости от текста HTTP-ошибки.
 */
export function isAccountCreationConfirmationRequired(error: unknown): boolean {
	if (!(error instanceof ProblemDetailsError) || error.status !== 409) {
		return false;
	}

	const extensions = error.details.extensions;
	if (!isRecord(extensions) || !Array.isArray(extensions.errors)) {
		return false;
	}

	return extensions.errors.some((item) =>
		isRecord(item) && item.code === FACEBOOK_ACCOUNT_CREATION_CONFIRMATION_REQUIRED
	);
}

/**
 * Запрашивает согласие на создание аккаунта только после ожидаемого lookup-only конфликта.
 */
export async function authenticateFacebookInstantGamesWithConfirmation(
	options: FacebookAuthOptions,
	authenticate: (currentOptions: FacebookAuthOptions) => Promise<void>,
	confirmCreation: (onPlay: () => Promise<void>) => Promise<boolean>
): Promise<boolean> {
	try {
		await authenticate(options);
		return true;
	} catch (error) {
		if (options.ExistingAccountOnly !== true ||
			!isAccountCreationConfirmationRequired(error)) {
			throw error;
		}

		return confirmCreation(async () => {
			const retryOptions = { ...options };
			delete retryOptions.ExistingAccountOnly;
			await authenticate(retryOptions);
		});
	}
}

/**
 * Собирает подписанные ASID игрока в связанных FB-приложениях. Данные необязательны: при отсутствии метода,
 * ошибке, пустом ответе или таймауте возвращает undefined и не мешает входу.
 */
export async function collectFacebookAssociatedApps(
	playerApi: FacebookAssociatedAppsPlayerApi,
	onUnavailable: (reason: FacebookAssociatedAppsUnavailableReason) => void = () => undefined,
	timeoutMs = FACEBOOK_ASSOCIATED_APPS_TIMEOUT_MS
): Promise<FacebookAssociatedAppAsid[] | undefined> {
	const getAssociatedApps = playerApi.getSignedAssociatedAppsASIDAsync;
	if (typeof getAssociatedApps !== "function") {
		onUnavailable("unsupported");
		return undefined;
	}

	let timer: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<"timeout">((resolve) => {
		timer = setTimeout(() => resolve("timeout"), timeoutMs);
	});

	try {
		// Метод SDK вызываем на объекте игрока: реализация Facebook может опираться на this
		const result = await Promise.race([getAssociatedApps.call(playerApi), timeout]);
		if (result === "timeout") {
			onUnavailable("timeout");
			return undefined;
		}

		if (result === null || result.length === 0) {
			onUnavailable("empty");
			return undefined;
		}

		return result.map((app) => ({
			AppId: app.getAppID(),
			Asid: app.getASID(),
			SignedRequest: app.getSignature()
		}));
	} catch {
		onUnavailable("error");
		return undefined;
	} finally {
		clearTimeout(timer);
	}
}
